import { api } from "../../../api/apiSlice";

// Branch-wise master (handoff §9.8) - same shape as Customers: each row
// belongs to exactly one branch, Product_Code is only branch-local (2,087 of
// 3,288 collide across branches), so every single-product route needs
// {branchId} alongside {productCode} to disambiguate. salesRate is computed
// server-side (purchaseRate + purchaseRate * markupPercentage / 100) - never
// sent, always returned.
export const productsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query({
      query: (params) => ({ url: "/masters/products", params }),
      // ProductListItemRecord now carries RecordGuid (2026-08-19, same
      // identity fix Employees got in Batch48) - tag on that directly
      // instead of the ambiguous productCode business key.
      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: "MasterProducts", id: p.recordGuid })),
              { type: "MasterProducts", id: "LIST" },
            ]
          : [{ type: "MasterProducts", id: "LIST" }],
    }),
    // GetById/Update switched from {branchId}/{productCode} to {recordGuid}
    // server-side (2026-08-19) - a real duplicate (BranchId, Product_Code)
    // pair (8 still on-prem) made the old business-key route throw
    // "Sequence contains more than one element" on Edit. Product_Details.
    // RecordGuid is a pre-existing, fully-populated, unique column. Every
    // other Products route (linked/branch-map/groups) is UNCHANGED - still
    // {branchId}/{productCode} - only GetById/Update moved.
    getProduct: builder.query({
      query: (recordGuid) => ({ url: `/masters/products/${recordGuid}` }),
      providesTags: (result, error, recordGuid) => [{ type: "MasterProducts", id: recordGuid }],
    }),
    createProduct: builder.mutation({
      query: (body) => ({ url: "/masters/products", method: "POST", data: body }),
      invalidatesTags: [{ type: "MasterProducts", id: "LIST" }],
    }),
    updateProduct: builder.mutation({
      query: ({ recordGuid, ...body }) => ({
        url: `/masters/products/${recordGuid}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error, { recordGuid }) => [
        { type: "MasterProducts", id: recordGuid },
        { type: "MasterProducts", id: "LIST" },
      ],
    }),
    // Groups are branch-wise too ({branchId}/{groupId} on write routes) but
    // the list endpoint returns every branch's groups together (no working
    // branchId filter server-side) - callers filter client-side by branchId.
    getProductGroups: builder.query({
      query: () => ({ url: "/masters/products/groups" }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((g) => ({ type: "MasterProductGroups", id: `${g.branchId}-${g.groupId}` })),
              { type: "MasterProductGroups", id: "LIST" },
            ]
          : [{ type: "MasterProductGroups", id: "LIST" }],
    }),
    createProductGroup: builder.mutation({
      query: (body) => ({ url: "/masters/products/groups", method: "POST", data: body }),
      invalidatesTags: [{ type: "MasterProductGroups", id: "LIST" }],
    }),
    updateProductGroup: builder.mutation({
      query: ({ branchId, groupId, ...body }) => ({
        url: `/masters/products/groups/${branchId}/${groupId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: [{ type: "MasterProductGroups", id: "LIST" }],
    }),
    deleteProductGroup: builder.mutation({
      query: ({ branchId, groupId }) => ({
        url: `/masters/products/groups/${branchId}/${groupId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "MasterProductGroups", id: "LIST" }],
    }),
    // Cross-branch linking (handoff §9.8/§13.2) - identical shape/semantics
    // to Customer linking, see LinkedProductsPanel.jsx.
    // 2026-09-18: branch-scoped server-side by default, same change as the customer
    // equivalent. Both current callers (duplicate check, link panel) are genuine
    // cross-branch jobs and pass crossBranch: true, which needs Products.Create|Edit.
    // A plain product picker must NOT pass it - use useLazyGetProductsQuery instead.
    searchProductsAllBranches: builder.query({
      query: (arg) => {
        const { query, crossBranch } = typeof arg === "string" ? { query: arg } : arg;
        return {
          url: "/masters/products/search-all-branches",
          params: { query, crossBranch: crossBranch || undefined },
        };
      },
    }),
    getLinkedProducts: builder.query({
      query: ({ branchId, productCode }) => ({
        url: `/masters/products/${branchId}/${productCode}/linked`,
      }),
      providesTags: (result, error, { branchId, productCode }) => [
        { type: "MasterProductLinks", id: `${branchId}-${productCode}` },
      ],
    }),
    linkProduct: builder.mutation({
      query: ({ branchId, productCode, targetBranchId, targetProductCode }) => ({
        url: `/masters/products/${branchId}/${productCode}/link`,
        method: "POST",
        data: { targetBranchId, targetProductCode },
      }),
      invalidatesTags: (result, error, { branchId, productCode }) => [
        { type: "MasterProductLinks", id: `${branchId}-${productCode}` },
      ],
    }),
    unlinkProduct: builder.mutation({
      query: ({ branchId, productCode }) => ({
        url: `/masters/products/${branchId}/${productCode}/link`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { branchId, productCode }) => [
        { type: "MasterProductLinks", id: `${branchId}-${productCode}` },
      ],
    }),
    // Branch mapping (handoff §13.5) - a different, additional capability
    // from linking above: the product stays ONE row at its home branchId,
    // this just marks which OTHER branches can also see/use it (no second
    // row created). Full-desired-list diff, same convention as guard
    // assignments - send the whole set of target branchIds each time.
    getProductBranchMap: builder.query({
      query: ({ branchId, productCode }) => ({
        url: `/masters/products/${branchId}/${productCode}/branch-map`,
      }),
      providesTags: (result, error, { branchId, productCode }) => [
        { type: "MasterProductBranchMap", id: `${branchId}-${productCode}` },
      ],
    }),
    setProductBranchMap: builder.mutation({
      query: ({ branchId, productCode, branchIds }) => ({
        url: `/masters/products/${branchId}/${productCode}/branch-map`,
        method: "PUT",
        data: { branchIds },
      }),
      invalidatesTags: (result, error, { branchId, productCode }) => [
        { type: "MasterProductBranchMap", id: `${branchId}-${productCode}` },
        { type: "MasterProducts", id: "LIST" },
      ],
    }),
    bulkSetProductWarranty: builder.mutation({
      query: (body) => ({ url: "/masters/products/bulk-warranty", method: "POST", data: body }),
      // Reflagging a group changes which products the AMC equipment pickers offer, so both
      // the product list and any open equipment screen need to re-read.
      invalidatesTags: [{ type: "MasterProducts", id: "LIST" }, { type: "AmcEquipment", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetProductGroupsQuery,
  useBulkSetProductWarrantyMutation,
  useCreateProductGroupMutation,
  useUpdateProductGroupMutation,
  useDeleteProductGroupMutation,
  useLazySearchProductsAllBranchesQuery,
  useGetLinkedProductsQuery,
  useLinkProductMutation,
  useUnlinkProductMutation,
  useGetProductBranchMapQuery,
  useSetProductBranchMapMutation,
} = productsApi;
